import { Clock3, User, MessageSquare } from 'lucide-react'
import { MaintenanceStatusEntry } from '../../types/maintenance'
import { format } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import { getStatusLabel } from '../../utils/status'

// Status badge colours matching MaintenanceForm getStatusBadgeClass
const STATUS_BADGE_CLASSES: Record<string, string> = {
  AGUARDANDO_TRIAGEM: 'bg-purple-50 text-purple-700 ring-1 ring-inset ring-purple-200',
  PENDENTE: 'bg-brand-warning-50 text-brand-warning-700 ring-1 ring-inset ring-brand-warning-200',
  APROVADO: 'bg-green-50 text-green-700 ring-1 ring-inset ring-green-200',
  EXECUTADO: 'bg-blue-50 text-blue-700 ring-1 ring-inset ring-blue-200',
  REJEITADO: 'bg-red-50 text-red-700 ring-1 ring-inset ring-red-200',
  CONCLUIDO: 'bg-emerald-50 text-emerald-700 ring-1 ring-inset ring-emerald-200',
  ARQUIVADO: 'bg-gray-50 text-gray-600 ring-1 ring-inset ring-gray-200',
}

function getStatusBadgeClass(status: string): string {
  return STATUS_BADGE_CLASSES[status?.toUpperCase()] ?? 'bg-gray-100 text-gray-600 ring-1 ring-inset ring-gray-200'
}

function formatRole(role: string): string {
  const ROLE_LABELS: Record<string, string> = {
    staff: 'Colaborador',
    supervisor: 'Supervisor',
    gerente: 'Gerente',
    cfo: 'CFO',
    admin: 'Administrador',
    sistema: 'Sistema',
    SISTEMA: 'Sistema',
  }
  return ROLE_LABELS[role] ?? role
}

interface StatusHistoryPanelProps {
  entries: MaintenanceStatusEntry[]
  /** Subcategoria do fluxo ('compras' mantém "Aprovado"; resto vira "Pendente do Responsável") */
  subcategory?: 'manutencao' | 'compras' | null
  className?: string
}

export default function StatusHistoryPanel({ entries, subcategory, className = '' }: StatusHistoryPanelProps) {
  if (!entries || entries.length === 0) {
    return (
      <div data-testid="history-panel" className={`rounded-lg border border-gray-200 bg-gray-50 p-4 ${className}`}>
        <div className="flex items-center gap-2 text-sm text-gray-500">
          <Clock3 className="h-4 w-4 flex-shrink-0" />
          <span>Sem histórico de estados registado.</span>
        </div>
      </div>
    )
  }

  // Show most recent first
  const sorted = [...entries].sort(
    (a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
  )

  return (
    <div data-testid="history-panel" className={`space-y-3 ${className}`}>
      {sorted.map((entry, idx) => {
        const ts = entry.timestamp instanceof Date ? entry.timestamp : new Date(entry.timestamp)
        const isValidDate = !isNaN(ts.getTime())

        return (
          <div
            key={idx}
            className="flex gap-3 rounded-lg border border-gray-200 bg-white p-3 shadow-sm"
          >
            {/* QA: sorted[0] is the most-recent entry (sort desc). Instrument idx===0 only to avoid
                duplicate testids. data-status carries the canonical enum token (no authorRole). */}
            {idx === 0 && (
              <span
                data-testid="status-history-latest"
                data-status={entry.status}
                style={{ display: 'none' }}
              />
            )}
            {/* Timeline dot */}
            <div className="mt-0.5 flex-shrink-0">
              <span className="flex h-2 w-2 rounded-full bg-brand-primary-400 mt-1.5" />
            </div>

            <div className="min-w-0 flex-1 space-y-1.5">
              {/* Top row: status badge + timestamp */}
              <div className="flex flex-wrap items-center gap-2">
                <span
                  className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${getStatusBadgeClass(entry.status)}`}
                >
                  {getStatusLabel(entry.status, subcategory)}
                </span>
                {isValidDate && (
                  <span className="flex items-center gap-1 text-xs text-gray-500">
                    <Clock3 className="h-3 w-3" />
                    {format(ts, "dd 'de' MMM yyyy, HH:mm", { locale: ptBR })}
                  </span>
                )}
              </div>

              {/* Author row */}
              <div className="flex items-center gap-1 text-xs text-gray-600">
                <User className="h-3 w-3 flex-shrink-0 text-gray-400" />
                <span className="font-medium">
                  {/* authorName is stored at runtime but not in the TS interface — access safely */}
                  {(entry as unknown as { authorName?: string }).authorName || entry.authorId}
                </span>
                {entry.authorRole && (
                  <span className="text-gray-400">· {formatRole(entry.authorRole)}</span>
                )}
              </div>

              {/* Notes */}
              <div className="flex items-start gap-1 text-xs text-gray-600">
                <MessageSquare className="mt-0.5 h-3 w-3 flex-shrink-0 text-gray-400" />
                <span className={entry.notes ? '' : 'italic text-gray-400'}>
                  {entry.notes || 'Sem observações'}
                </span>
              </div>
            </div>
          </div>
        )
      })}
    </div>
  )
}
